import { useCallback, useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import useTranslate from "hooks/useTranslate";
import GlobeIcon from "assets/icons/globe.svg?react";
import useClickOutside from "hooks/useClickOutside";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import { useBreakpoint } from "hooks/useBreakpoint";
import { useFormContext } from "react-hook-form";
import { usePhoneCountries } from "queries/usePhoneCountries";
import { PhoneCountry } from "types/PhoneCountries";
import { usePhoneCountriesStore } from "stores/phoneCountries";
import { useCollectPhoneStore } from "stores/collectPhone";
import InputMask, { InputMaskProps } from "../InputMask";
import { InputPhoneCountries } from "./components/InputPhoneCountries";
import "./styles.sass";
import { getCountryByPhoneValue } from "./utils/getCountryByPhoneValue";
import { validatePhoneNumber } from "./utils/validatePhoneNumber";
import { getCurrentCountry } from "./utils/getCurrentCountry";

export type InputPhoneProps = {
  value: string;
  phoneCountry: Nullable<PhoneCountry>;
  countryName: string;
  error: string;
  onCountryChange: (country: PhoneCountry) => void;
  onBlur?: (value: string, props: InputMaskProps) => void;
};

const InputPhone = ({
  value,
  phoneCountry,
  countryName,
  error,
  onCountryChange,
  onBlur,
}: InputPhoneProps) => {
  const t = useTranslate();
  const navigate = useNavigate();

  const { isDesktop } = useBreakpoint();
  const { setValue } = useFormContext();

  const { data: countries } = usePhoneCountries();
  const isPhoneCountriesOpen = usePhoneCountriesStore(
    (state) => state.isPhoneCountriesOpen,
  );
  const isPhoneCountryClicked = usePhoneCountriesStore(
    (state) => state.isPhoneCountryClicked,
  );
  const setIsPhoneCountriesOpen = usePhoneCountriesStore(
    (state) => state.setIsPhoneCountriesOpen,
  );
  const setIsPhoneCountryClicked = usePhoneCountriesStore(
    (state) => state.setIsPhoneCountryClicked,
  );

  const { isModalOpen } = useCollectPhoneStore();

  const [isActive, setIsActive] = useState(false);
  const [mask, setMask] = useState("");

  const countriesRef = useRef<HTMLDivElement>(null);
  const flagRef = useRef<HTMLDivElement>(null);

  const handleFlagClick = useCallback(() => {
    if (isDesktop) {
      setIsPhoneCountriesOpen(!isPhoneCountriesOpen);
    } else {
      navigate({
        search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.phoneCountries })}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isPhoneCountriesOpen, setIsPhoneCountriesOpen]);

  const handleFlagClose = useCallback(() => {
    setIsPhoneCountriesOpen(false);
  }, [setIsPhoneCountriesOpen]);

  const handleFocus = useCallback(() => {
    setMask(phoneCountry?.phoneNumberMask || "");
    setIsActive(true);
  }, [phoneCountry?.phoneNumberMask]);

  const handleBLur = useCallback(
    (currentValue: string, props: InputMaskProps) => {
      if (onBlur) {
        onBlur(currentValue, props);
      }

      if (!currentValue) {
        setIsActive(false);
      }
    },
    [onBlur],
  );

  useEffect(() => {
    if (!isModalOpen && countries) {
      const currentCountry = getCurrentCountry(countryName, countries);

      if (value) {
        const country = getCountryByPhoneValue(value, countries);
        if (country) {
          onCountryChange(country);
          return;
        }
      }

      if (currentCountry) {
        onCountryChange(currentCountry);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, isModalOpen]);

  useEffect(() => {
    if (isPhoneCountryClicked) {
      if (phoneCountry?.phoneNumberMask) {
        setValue("phoneNumber", " ");
        setMask(phoneCountry.phoneNumberMask);
      }

      handleFlagClose();
      setIsPhoneCountryClicked(false);
    }
  }, [
    handleFlagClose,
    isPhoneCountryClicked,
    phoneCountry?.phoneNumberMask,
    setIsPhoneCountryClicked,
    setValue,
  ]);

  useClickOutside({
    refs: [countriesRef, flagRef],
    handler: () => isDesktop && handleFlagClose(),
  });

  return (
    <div className="InputPhone">
      <InputMask
        type="tel"
        label={t("profile.account.phone")}
        placeholder={t("profile.account.phone")}
        name="phoneNumber"
        mask={mask}
        value={value}
        error={error ? t("profile.account.phone.error.format") : ""}
        lazy={!isActive}
        validation={{
          validate: (currentValue) =>
            validatePhoneNumber(currentValue || value, phoneCountry, t),
        }}
        onBlur={handleBLur}
        onFocus={handleFocus}
      />

      <div className="InputPhone__icon" onClick={handleFlagClick} ref={flagRef}>
        {phoneCountry?.flagUrl ? (
          <img src={phoneCountry?.flagUrl} alt={phoneCountry.name} />
        ) : (
          <GlobeIcon />
        )}
      </div>

      {isDesktop && <InputPhoneCountries ref={countriesRef} />}
    </div>
  );
};

export default InputPhone;
